import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { TFunction } from 'i18next';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { RebalanceInsightAiContext } from '../../types/insightNarrative.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import {
  getReportClientLabel,
  getReportGoalLabel,
} from '../presentation/reportLabels';
import type { ReportSubAccountDisplayMetadata } from '../snapshot/buildReportDisplayMetadata';
import { AdvisorSituationKey } from './advisorSituationKeys';
import { buildPortfolioAdvisorInsights } from './buildPortfolioAdvisorInsights';
import { buildPortfolioDecisionSummariesForAi } from './buildPortfolioDecisionSummariesForAi';
import { buildRebalanceChangeSummary } from './buildRebalanceChangeSummary';
import { buildTaxLocationAccountFlows } from './buildTaxLocationAccountFlows';
import {
  buildTopInsightMovesForAi,
  collectInsightAccountTypes,
} from './buildTopInsightMoves';
import { compactInsightAiContext, takeFirst } from './compactInsightAiContext';
import {
  buildSubAccountNamesById,
  formatAdvisorDecisionAction,
  resolveAdvisorSituationKey,
} from './formatAdvisorInsightText';

const TRIGGER_LABEL: Record<string, string> = {
  drift: 'Drift',
  liquidate: 'Liquidation',
  transfer: 'Pending withdrawal',
};

const STRING_ARRAY_CAP = 50;
const NESTED_ARRAY_CAP = 25;

export const canGeneratePortfolioInsightNarrative = (
  audit?: OptimizerAuditV2Record,
  unreliable = false,
): boolean => !!audit && audit.passed !== false && !unreliable;

export const buildPortfolioRebalanceInsightAiContext = ({
  audit,
  report,
  diffRows = [],
  subAccountsById = new Map(),
  clientLabel,
  goalLabel,
  t,
}: {
  audit?: OptimizerAuditV2Record;
  report?: PortfolioReport;
  diffRows?: WeightDiffDetailRow[];
  subAccountsById?: Map<string, ReportSubAccountDisplayMetadata>;
  clientLabel?: string;
  goalLabel?: string;
  t: TFunction;
}): RebalanceInsightAiContext => {
  const insights = buildPortfolioAdvisorInsights(audit);
  const changes = buildRebalanceChangeSummary(report, diffRows, audit);
  const namesById = buildSubAccountNamesById(subAccountsById, audit);
  const taxLocationMoves = buildTaxLocationAccountFlows(audit, namesById);
  const portfolioLabel =
    [clientLabel, goalLabel].filter(Boolean).join(' · ') ||
    (report
      ? [getReportClientLabel(report), getReportGoalLabel(report)]
          .filter(Boolean)
          .join(' · ')
      : undefined);
  const usedCashModeSubAccountIds = new Set<string>();
  const totalBuyCents = (audit?.subAccountResults ?? []).reduce(
    (sum, result) => sum + Math.max(0, result.totalBuyCents ?? 0),
    0,
  );
  const totalSellCents = (audit?.subAccountResults ?? []).reduce(
    (sum, result) => sum + Math.max(0, result.totalSellCents ?? 0),
    0,
  );
  const totalTradeCount = (audit?.subAccountResults ?? []).reduce(
    (sum, result) => sum + Math.max(0, result.tradeCount ?? 0),
    0,
  );
  const advisorNotes = takeFirst(
    (insights?.decisions ?? [])
      .filter((decision) => {
        const key = resolveAdvisorSituationKey(decision);
        return (
          key !== AdvisorSituationKey.CashRaise &&
          key !== AdvisorSituationKey.CashDeploy
        );
      })
      .map((decision) =>
        formatAdvisorDecisionAction(decision, namesById, audit, t, {
          diffRows,
          usedCashModeSubAccountIds,
        }),
      )
      .filter((note): note is string => Boolean(note)),
    STRING_ARRAY_CAP,
  );
  const { largestBuys, largestSells } = buildTopInsightMovesForAi(
    diffRows,
    subAccountsById,
  );
  const decisions = buildPortfolioDecisionSummariesForAi({
    audit,
    changes,
    diffRows,
    subAccountsById,
    taxLocationMoveCount: taxLocationMoves.length,
    t,
  });

  return compactInsightAiContext({
    scope: 'PORTFOLIO',
    portfolioLabel: portfolioLabel || undefined,
    rebalanceMode: audit?.rebalanceMode ?? undefined,
    effectiveRebalanceMode: audit?.effectiveRebalanceMode ?? undefined,
    accountTypes: takeFirst(
      collectInsightAccountTypes(audit),
      STRING_ARRAY_CAP,
    ),
    largestBuys: takeFirst(largestBuys, STRING_ARRAY_CAP),
    largestSells: takeFirst(largestSells, STRING_ARRAY_CAP),
    triggers: takeFirst(
      changes.triggers.map((trigger) => TRIGGER_LABEL[trigger] ?? trigger),
      STRING_ARRAY_CAP,
    ),
    cashRaiseCents: changes.displayCashRaiseCents || undefined,
    cashDeployCents: changes.displayCashDeployCents || undefined,
    totalBuyCents,
    totalSellCents,
    totalTradeCount: totalTradeCount || undefined,
    newPositions: takeFirst(changes.newPositions, STRING_ARRAY_CAP),
    exits: takeFirst(changes.exits, STRING_ARRAY_CAP),
    registeredSellCents: insights?.taxInsight?.registeredSellCents,
    taxableSellCents: insights?.taxInsight?.taxableSellCents,
    turnoverPct: insights?.turnoverPct ?? undefined,
    turnoverBuyPct: insights?.turnoverBreakdown?.buyPct,
    turnoverSellPct: insights?.turnoverBreakdown?.sellPct,
    taxLocationMoves: takeFirst(
      taxLocationMoves.map((flow) => ({
        fromAccountLabel: flow.fromAccount,
        toAccountLabel: flow.toAccount,
        amountCents: flow.amountCents,
      })),
      NESTED_ARRAY_CAP,
    ),
    advisorNotes,
    decisions,
  });
};
